import api from '@/api/api';
import { createQueryParams } from '@/utils/createQueryParams';

const DOMAIN = '/professional-private';

const login = async ({
	email,
	password,
}: {
	email: string;
	password: string;
}): Promise<Professionals.LoginResponse> => {
	const { data } = await api.post(`${DOMAIN}/login`, { email, password });
	return data;
};

const getByRole = async (
	params: Professionals.ProfessionalParams,
): Promise<Professionals.GetProfessionalsRespose> => {
	const queryParams = createQueryParams({ ...params });
	const { data } = await api.get(`${DOMAIN}?${queryParams}`);
	return data;
};

const get = async (
	params: Professionals.ProfessionalParams,
): Promise<Professionals.GetProfessionalsRespose> => {
	const { permission_tags, ...rest } = params;
	const queryParams = createQueryParams({ ...rest });
	if (permission_tags?.length) {
		for (const tag of permission_tags) {
			queryParams.append('permission_tags', tag);
		}
	}
	const { data } = await api.get(`${DOMAIN}/all?${queryParams}`);
	return data;
};

const getById = async (id: string): Promise<Professionals.Professional> => {
	const { data } = await api.get(`${DOMAIN}/${id}?getClinicAddress=true`);
	return data;
};

const getMe = async (): Promise<Professionals.Professional> => {
	const { data } = await api.get(`${DOMAIN}/me`);
	return data;
};

const create = async (
	body: Professionals.Create,
): Promise<Professionals.Professional> => {
	const { data } = await api.post(`${DOMAIN}`, body);
	return data;
};

const patch = async (
	body: Partial<Professionals.Professional>,
	id: string,
): Promise<Professionals.Professional> => {
	const { data } = await api.patch(`${DOMAIN}/${id}`, body);
	return data;
};

const pathAuthorization = async (
	id: string,
	body: { authorized: boolean },
): Promise<Professionals.Professional> => {
	const { data } = await api.patch(`${DOMAIN}/${id}/authorization`, body);
	return data;
};

const patchPermissionTags = async (
	id: string,
	body: Professionals.UpdatePermissionTags,
): Promise<Professionals.PermissionTagsUpdateResponse> => {
	const { data } = await api.patch(`${DOMAIN}/${id}/permission-tags`, body);
	return data;
};

const patchVisibility = async (
	id: string,
	body: Professionals.UpdateVisibility,
): Promise<Professionals.Professional> => {
	const { data } = await api.patch(`${DOMAIN}/${id}/visibility`, body);
	return data;
};

/** Lista (Admin) dos profissionais visíveis ao usuário B2C, na ordem de exibição atual. */
const getDisplayOrderList = async (
	params?: Professionals.DisplayOrderParams,
): Promise<Array<Professionals.DisplayOrderItem>> => {
	const qs = params?.service_id
		? `?${createQueryParams({ service_id: params.service_id })}`
		: '';
	const { data } = await api.get(`${DOMAIN}/display-order${qs}`);
	return data;
};

/** Grava a nova ordem de exibição (o índice de cada id vira o display_order). */
const reorderDisplay = async (
	body: Professionals.ReorderDisplay,
): Promise<Array<Professionals.DisplayOrderItem>> => {
	const { data } = await api.put(`${DOMAIN}/display-order`, body);
	return data;
};

const logout = async (): Promise<void> => {
	await api.delete(`${DOMAIN}/logout`);
};

const getMonthlyUsage = async (
	id: string,
	params?: Professionals.MonthlyUsageParams,
): Promise<Professionals.MonthlyUsageListResponse> => {
	const queryParams = createQueryParams({ ...(params ?? {}) });
	const { data } = await api.get(
		`${DOMAIN}/${id}/monthly-usage?${queryParams}`,
	);
	return data;
};

const ProfessionalsService = {
	login,
	getByRole,
	get,
	getById,
	getMonthlyUsage,
	create,
	patch,
	pathAuthorization,
	patchPermissionTags,
	patchVisibility,
	getDisplayOrderList,
	reorderDisplay,
	getMe,
	logout,
};

export { ProfessionalsService };
